import { useCallback, useMemo, useState } from 'react';

import { useEventInfoWithPlayersQuery, useLeagueUpcomingEventsQuery } from '@/api/events/query.generated';
import { useUpdatePlayerStoreWithNewData } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { useSinglePlayerProfile } from '@/feature/fantasy-event-sheets/SinglePlayerProfileProvider';
import { CardSegmentTitleType } from '@/feature/fantasy-event-sheets/components/ufc-events/CardSegmentTitle';
import { useGQLRequestPolicy } from '@/feature/guest-user/hooks/use-gql-request-policy';
import { useLeagueContentRenderer } from '@/feature/lobby/hooks/use-league-content-render';
import { useResumeEffectWithDelay } from '@/hooks/use-resume';
import { CardSegment, EventAttributesType, IndividualVersusEvent, League } from '@/types/api.generated';
import { findEventAttribute } from '@/utils/fantasy-attribute-utils';
import { isUFC } from '@/utils/league';
import { sortEventsByDate } from '@/utils/sortEvents';

import { FILTER_MODES_TABS, UFC_FILTER_MODES_TABS } from '../utils/filters';
import { useSortedLobbyPlayers } from './use-lobby-data';

/**
 * Hook to compute the data to be displayed on the event details screen based on the selected event.
 *
 * This hook will fetch the required event data.
 * It will also return the render functions to render the data.
 */
export const useFantasyEventContent = ({
    league,
    eventId,
    isActive,
}: {
    league: League;
    eventId: string;
    isActive: boolean;
}) => {
    const [filterType, setFilterMode] = useState(
        league === League.Ufc ? UFC_FILTER_MODES_TABS[0].filterType : FILTER_MODES_TABS[0].filterType
    );
    const requestPolicy = useGQLRequestPolicy('cache-and-network');

    // on UFC, we show players from all the available events
    // on other leagues, we show players from the selected event
    const [{ data: eventWithPlayers, fetching }, fetchEventData] = useEventInfoWithPlayersQuery({
        variables: { id: eventId ?? '' },
        requestPolicy,
        // for UFC: Always pause this query (use the league query instead)
        // for other leagues: Run this query only when isActive is true
        pause: isUFC(league) ? true : !isActive,
    });

    const [{ data: ufcEvents, fetching: ufcFetching }, fetchUfcEventsDetails] = useLeagueUpcomingEventsQuery({
        variables: { league },
        requestPolicy,
        // for UFC leagues: Run this query only when isActive is true
        // for other leagues: Always pause this query
        pause: isUFC(league) ? !isActive : true,
    });

    const eventData = useMemo(() => {
        return (isUFC(league) ? ufcEvents?.getUpcomingEventsV2 || [] : [eventWithPlayers?.getEventByIdV2]).filter(
            Boolean
        );
    }, [eventWithPlayers?.getEventByIdV2, league, ufcEvents?.getUpcomingEventsV2]);

    // don't use here useLiveEventsPropsUpdate since we already subscribe to event updates in FantasyEventSheets
    useUpdatePlayerStoreWithNewData(eventData);

    const hasStaleData =
        eventWithPlayers?.getEventByIdV2 !== undefined && eventWithPlayers.getEventByIdV2.id !== eventId;

    const eventsData = useMemo(() => {
        if (isUFC(league)) {
            return ufcEvents?.getUpcomingEventsV2 || [];
        }
        return hasStaleData ? [] : [eventWithPlayers?.getEventByIdV2].filter(Boolean);
    }, [eventWithPlayers?.getEventByIdV2, hasStaleData, league, ufcEvents?.getUpcomingEventsV2]);

    const sortedPlayers = useSortedLobbyPlayers({
        sortedEvents: eventsData,
        filterType,
        league,
    });

    const refreshData = useCallback(() => {
        if (isActive) {
            if (isUFC(league)) {
                fetchUfcEventsDetails();
                return;
            }
            fetchEventData();
        }
    }, [fetchEventData, fetchUfcEventsDetails, isActive, league]);

    const { openSinglePlayerProfile } = useSinglePlayerProfile();
    useResumeEffectWithDelay(refreshData);

    const { renderPlayerItem, renderEventCards, renderFilters, renderLeagueFooter } = useLeagueContentRenderer({
        league,
        selectedFilter: filterType,
        selectFilter: setFilterMode,
        sortedEvents: eventsData,
        sortedPlayers,
        eventsLoading: isUFC(league) ? ufcFetching : fetching || hasStaleData,
        hideGameTime: true,
        openSinglePlayerProfile,
    });

    // used for "Fights" tab in UFC
    const sortedUfcEvents = useMemo(() => {
        const ufcEventsArray = ufcEvents?.getUpcomingEventsV2 || [];

        const groupedBySegment = ufcEventsArray
            .sort(sortEventsByDate)
            .reduce<Record<CardSegment, IndividualVersusEvent[]>>((prev, event) => {
                const cardSegment: CardSegment | undefined =
                    (findEventAttribute(event, EventAttributesType.CardSegment) as CardSegment) ?? CardSegment.Prelims;

                return {
                    ...prev,
                    [cardSegment]: [...(prev[cardSegment] ?? []), event as IndividualVersusEvent],
                };
            }, {} as Record<CardSegment, IndividualVersusEvent[]>);

        const eventsWithSegments: (CardSegmentTitleType | IndividualVersusEvent)[] = UFC_CARD_SEGMENT_ORDER.flatMap(
            cardSegment => {
                return groupedBySegment[cardSegment]?.length
                    ? [
                          {
                              // add typename property for easier type checking
                              __typename: 'CardSegmentTitle',
                              cardSegment,
                              // for each card segment show the first event date as the startDate of the card
                              startDate: groupedBySegment[cardSegment][0].date,
                              tournamentStartDate: findEventAttribute(
                                  groupedBySegment[cardSegment][0],
                                  EventAttributesType.TournamentStartDateTime
                              ),
                          },
                          //UFC events within cards should be sorted such that the last fight is first
                          ...groupedBySegment[cardSegment].sort(sortEventsByDate).reverse(),
                      ]
                    : [];
            }
        );

        return eventsWithSegments;
    }, [ufcEvents?.getUpcomingEventsV2]);

    return {
        sortedPlayers,
        sortedUfcEvents,
        filterType,
        renderPlayerItem,
        renderEventCards,
        renderFilters,
        renderLeagueFooter,
    };
};

const UFC_CARD_SEGMENT_ORDER = [CardSegment.MainCard, CardSegment.Prelims, CardSegment.EarlyPrelims];
